<script lang="ts">
  import type { SerializedPagination } from '../types'
  import { getI18n } from '../i18n'
  import { inertia } from '../utils/inertia'

  // Real links, so pages can be opened in a new tab and crawled. Keeps the search field's state across pages.
  let { pagination, pageHref }: { pagination: SerializedPagination; pageHref: (pageNumber: number) => string } = $props()

  const i18n = getI18n()

  type PaginationItem = number | 'ellipsis'

  function buildPaginationItems(current: number, total: number): PaginationItem[] {
    const pageNumbers = new Set<number>([1, total, current])
    if (current > 1) pageNumbers.add(current - 1)
    if (current < total) pageNumbers.add(current + 1)

    const sorted = [...pageNumbers].sort((a, b) => a - b)
    const items: PaginationItem[] = []
    sorted.forEach((page, index) => {
      if (index > 0 && page - sorted[index - 1] > 1) items.push('ellipsis')
      items.push(page)
    })
    return items
  }

  const paginationItems = $derived(buildPaginationItems(pagination.currentPage, pagination.totalPages))
  const rangeStart = $derived((pagination.currentPage - 1) * pagination.perPage + 1)
  const rangeEnd = $derived(Math.min(pagination.currentPage * pagination.perPage, pagination.totalCount))
  const previousHref = $derived(pagination.currentPage > 1 ? pageHref(pagination.currentPage - 1) : null)
  const nextHref = $derived(pagination.currentPage < pagination.totalPages ? pageHref(pagination.currentPage + 1) : null)
</script>

{#snippet previousIcon()}
  <svg class="pagination-arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
{/snippet}

{#snippet nextIcon()}
  <svg class="pagination-arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
{/snippet}

{#if pagination.totalPages > 1}
  <nav class="pagination" aria-label={i18n.t('pagination.label')}>
    <div class="pagination-row">
      {#if previousHref}
        <a class="pagination-arrow" href={previousHref} {@attach inertia({ href: previousHref, preserveState: true })} aria-label={i18n.t('pagination.previous')}>
          {@render previousIcon()}
        </a>
      {:else}
        <span class="pagination-arrow -disabled">{@render previousIcon()}</span>
      {/if}
      {#each paginationItems as item, index (index)}
        {#if item === 'ellipsis'}
          <span class="pagination-ellipsis">…</span>
        {:else}
          {@const href = pageHref(item)}
          <a
            class={['pagination-button', { '-current': item === pagination.currentPage }]}
            {href}
            {@attach inertia({ href, preserveState: true })}
            aria-label={i18n.t('pagination.page', { page: item })}
            aria-current={item === pagination.currentPage ? 'page' : undefined}
          >
            {item}
          </a>
        {/if}
      {/each}
      {#if nextHref}
        <a class="pagination-arrow" href={nextHref} {@attach inertia({ href: nextHref, preserveState: true })} aria-label={i18n.t('pagination.next')}>
          {@render nextIcon()}
        </a>
      {:else}
        <span class="pagination-arrow -disabled">{@render nextIcon()}</span>
      {/if}
    </div>
    <p class="pagination-summary">{i18n.t('pagination.summary', { start: rangeStart, end: rangeEnd, total: pagination.totalCount })}</p>
  </nav>
{/if}

<style>
  .pagination {
    --pagination-control-size: 36px;

    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-5);
    padding-block: var(--space-10);
  }

  .pagination-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .pagination-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    width: var(--pagination-control-size);
    height: var(--pagination-control-size);
    border: none;
    border-radius: var(--radius-3);
    background-color: transparent;
    color: var(--color-text-muted);
    transition: background-color var(--transition-fast);

    &:hover:not(.-disabled) {
      background-color: var(--color-bg-muted);
    }

    &.-disabled {
      opacity: var(--opacity-1);
      cursor: not-allowed;
    }
  }

  .pagination-arrow-icon {
    width: 18px;
    height: 18px;

    &:dir(rtl) {
      transform: scaleX(-1);
    }
  }

  .pagination-button {
    display: flex;
    align-items: center;
    justify-content: center;
    font: inherit;
    font-size: var(--font-size-4);
    font-weight: var(--font-weight-3);
    min-width: var(--pagination-control-size);
    height: var(--pagination-control-size);
    padding: 0 var(--space-5);
    border: none;
    border-radius: var(--radius-3);
    background-color: transparent;
    color: var(--color-text);
    text-decoration: none;
    transition: background-color var(--transition-fast);

    &:hover:not(.-current) {
      background-color: var(--color-bg-muted);
    }

    &.-current {
      background-color: var(--color-variant-brand);
      color: var(--color-variant-brand-muted);
      cursor: default;
    }
  }

  .pagination-ellipsis {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: var(--pagination-control-size);
    height: var(--pagination-control-size);
    color: var(--color-text-muted);
  }

  .pagination-summary {
    font-size: var(--font-size-4);
    color: var(--color-text-muted);
    margin: 0;
  }
</style>
